import { UpdateResolver } from '@urql/exchange-graphcache';
import { gql } from 'urql';
import {
  CreateHistoryEventMutation,
  CreateHistoryEventMutationVariables,
  UpdateHistoryEventMutation,
  UpdateHistoryEventMutationVariables,
  HistoryEventDeepFragment,
  GetEventGroupsForProjectDocument,
} from '../../gql/graphql';

export const createHistoryEventResolver: UpdateResolver = (
  result: CreateHistoryEventMutation,
  args: CreateHistoryEventMutationVariables,
  cache
) => {
  const newEvent = result.createHistoryEvent?.historyEvent;
  if (!newEvent) return;

  const allFields = cache.inspectFields('Query');
  const matching = allFields.filter(
    (field) =>
      field.fieldName === 'historyEventsConnection' &&
      (field.arguments?.condition as any)?.projectId === newEvent.projectId
  );

  for (const field of matching) {
    cache.invalidate('Query', 'historyEventsConnection', field.arguments);
  }

  // Invalidate the events groups cache as well.
  const eventGroupsMatching = allFields.filter(
    (field) =>
      field.fieldName === 'getHistoryEventGroups' &&
      (field.arguments as any)?.projectId === newEvent.projectId
  );
  for (const field of eventGroupsMatching) {
    cache.invalidate('Query', 'getHistoryEventGroups', field.arguments);
  }

  // If this is a checkpoint, invalidate checkpoint queries
  if (newEvent.isCheckpoint) {
    const checkpointMatching = allFields.filter(
      (field) =>
        field.fieldName === 'historyEvents' &&
        (field.arguments?.condition as any)?.projectId === newEvent.projectId &&
        (field.arguments?.condition as any)?.isCheckpoint === true
    );
    for (const field of checkpointMatching) {
      cache.invalidate('Query', 'historyEvents', field.arguments);
    }
  }
};

export const updateHistoryEventResolver: UpdateResolver = (
  result: UpdateHistoryEventMutation,
  args: UpdateHistoryEventMutationVariables,
  cache
) => {
  // We do not fetch the events individually
  // What we currently fetch is history groups
  // what we want to do is grab and patch them.
  if (result.updateHistoryEvent?.historyEvent) {
    const eventId = result.updateHistoryEvent.historyEvent.id;
    // Get the existing event from the cache
    const eventsForProject = cache.resolve(
      {
        __typename: 'Project',
        id: result.updateHistoryEvent.historyEvent.projectId,
      },
      'historyEvents'
    ) as HistoryEventDeepFragment[] | null;

    const existingEvent = eventsForProject?.find((item) => item.id === eventId);

    if (existingEvent) {
      // Merge the update with the existing event
      const updatedEvent = {
        ...existingEvent,
        ...result.updateHistoryEvent.historyEvent,
      };

      // Write the updated event to the cache
      cache.writeFragment(
        gql`
          fragment _ on HistoryEvent {
            id
            projectId
            author
            createdAt
            updatedAt
            eventName
            eventType
            resourceName
            resourceType
            resolved
            stateKey
            metadata
          }
        `,
        updatedEvent
      );
    }

    // Patch the events groups cache, we were initially invalidating
    const allFields = cache.inspectFields('Query');
    const eventGroupsMatching = allFields.filter(
      (field) =>
        field.fieldName === 'getHistoryEventGroups' &&
        (field.arguments as any)?.projectId ===
          result.updateHistoryEvent?.historyEvent?.projectId
    );
    for (const field of eventGroupsMatching) {
      // cache.invalidate('Query', 'getHistoryEventGroups', field.arguments);
      cache.updateQuery(
        {
          query: GetEventGroupsForProjectDocument,
          variables: { ...(field.arguments as any) },
        },
        (data) => {
          // If the current piece of data is the one that has been updated, patch the cache
          const groupSize = data?.getHistoryEventGroups?.length;
          if (!groupSize) return data;
          for (let i = 0; i < groupSize; i++) {
            if (!data?.getHistoryEventGroups?.[i]) continue;
            const group = data.getHistoryEventGroups[i];
            const updatedEvent = result.updateHistoryEvent?.historyEvent;
            if (group.firstEvent?.id === eventId) {
              data.getHistoryEventGroups[i] = {
                ...group,
                author: updatedEvent?.author ?? group.author,
                firstEvent: {
                  ...group.firstEvent,
                  ...updatedEvent,
                },
              };
            }
          }
          return data;
        }
      );
    }
  }
};
